/**
 * CreateQuestionGuide: the visual guide of the question form, opened by the "?" after the title of the create page
 * (/my-questions/create) and of the edit pages (/my-questions/edit/:uuid, /my-questions/draft/:uuid) (HelpTitle
 * helpId="create_question"). The steps follow the form from top to bottom: the author's task (the create page's
 * WriteTaskReminder, above the form on every step), the grade and subject (the create page's first step, with Back
 * leaving the form), the term / topic / subtopic / objective pickers, the A / B / C difficulty, the question and its
 * options, the correct answer, a question for a shared passage, and the buttons that keep or send it (the create
 * page's steps all have Back: the first leaves the form, the others go one step back and keep what was written).
 * Props, from the page: `mode` ("create", "edit" for a question, "draft" for a draft: the buttons at the bottom and the
 * first step differ), `inSubject` (the question is in an exam's subject, where the subtopic and difficulty are required
 * and the subject comes from the folder), `linked` (the question is written for, or linked to, a passage, which the
 * page shows above the form).
 * The task step is for readers who can be given a write task (a subject's author, expert or moderator) creating in a
 * subject: the page shows the reminder only when they have one there, so its text says "if".
 * It follows the screen as the form does: under 768px an option's "correct" tick (with its label) goes under its text,
 * and under 640px the buttons at the bottom take the whole width, stacked, the main one on top.
 */
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  EllipsisVerticalIcon,
  KeyIcon,
  PencilIcon,
  PhotoIcon,
  PlusIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckedIcon } from "@heroicons/react/20/solid";
import { HelpFlow, HelpIntro, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  Mark,
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniDialog,
  MiniDivider,
  MiniField,
  MiniIcon,
  MiniLevel,
  MiniRow,
  MiniStack,
  MiniTabs,
  MiniText,
} from "../Mini";
import { difficultyLabel } from "../../Difficulty/Difficulty";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";
import { UserContext } from "../../../utils/contexts/UserContext";
import { canSeeTasksIn } from "../../../utils/folders";

export default function CreateQuestionGuide({ mode = "create", inSubject = false, linked = false }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  // The form's breakpoints: an option's buttons go under its text below md, the bottom buttons stack below sm.
  const wide = useMediaQuery("(min-width: 768px)");
  const roomy = useMediaQuery("(min-width: 640px)");
  const ex = (key) => t(`help.common.example.${key}`);
  const own = (key) => t(`help.create_question.example.${key}`);
  const creating = mode === "create";
  const editing = mode === "edit";
  // Write tasks go to a subject's authors, experts and moderators (TaskDialog), never to the admin role itself.
  const taskable = (user?.folder_roles || []).some((role) => role !== "admin" && canSeeTasksIn(role));

  const back = t("buttons.back");
  const next = t("buttons.next");
  const draft = t("my_questions.actions.save_draft_and_exit");
  const finish = t("my_questions.actions.finish_and_create");
  const save = t("my_questions.actions.save_and_exit");
  const addOption = t("my_questions.editor.add_option");
  const correctLabel = t("my_questions.editor.mark_correct");
  const levels = ["A", "B", "C"].map((level) => difficultyLabel(t, level));

  // An answer option as the form shows it (two options: neither has its remove button yet): the drag handle, the text
  // and the "correct" tick, green once pressed; on the right of the text, or under it on phones, with its label.
  const option = (letter, text, correct = false) =>
    wide ? (
      <MiniRow className="flex-nowrap">
        <MiniIcon icon={EllipsisVerticalIcon} />
        <MiniField value={`${letter}) ${text}`} className="flex-1" />
        {correct ? (
          <Mark pointer round="full">
            <MiniIcon icon={CheckedIcon} tone="green" />
          </Mark>
        ) : (
          <MiniIcon icon={CheckCircleIcon} />
        )}
      </MiniRow>
    ) : (
      <MiniStack tight>
        <MiniRow className="flex-nowrap">
          <MiniIcon icon={EllipsisVerticalIcon} />
          <MiniField value={`${letter}) ${text}`} className="flex-1" />
        </MiniRow>
        <MiniRow>
          {correct ? (
            <MiniBadge tone="green" icon={CheckIcon} mark>
              {correctLabel}
            </MiniBadge>
          ) : (
            <MiniButton variant="outline" icon={CheckIcon}>
              {correctLabel}
            </MiniButton>
          )}
        </MiniRow>
      </MiniStack>
    );

  // The buttons at the bottom of the form: in a row on the right, or stacked on phones with the main one on top (its
  // marker without the cursor there, which would cover the button under it). The create page's steps have Back too:
  // on the left, or last on phones.
  const backButton = (
    <MiniButton variant="outline" icon={ArrowLeftIcon}>
      {back}
    </MiniButton>
  );
  const footer = (main, other, withBack = false) =>
    roomy ? (
      <MiniRow className={`${withBack ? "justify-between" : "justify-end"} gap-y-3`}>
        {withBack && backButton}
        <MiniRow className="justify-end gap-y-3">
          {other && <MiniButton variant="outline">{other}</MiniButton>}
          <MiniButton variant="primary" mark>
            {main}
          </MiniButton>
        </MiniRow>
      </MiniRow>
    ) : (
      <MiniStack>
        <MiniRow>
          <MiniButton variant="primary" mark={other || withBack ? { pointer: false } : true}>
            {main}
          </MiniButton>
        </MiniRow>
        {other && (
          <MiniRow>
            <MiniButton variant="outline">{other}</MiniButton>
          </MiniRow>
        )}
        {withBack && <MiniRow>{backButton}</MiniRow>}
      </MiniStack>
    );

  // The create page's reminder of the reader's write task (WriteTaskReminder): what to write, how many of each level
  // are needed and approved, and what counts (its rules box, marked).
  const taskLevel = (level, planned, approved) => (
    <MiniRow key={level} className="flex-nowrap">
      <MiniLevel level={level} points={false} />
      <MiniText>{t("folders.tasks.reminder.level_count", { planned, approved })}</MiniText>
    </MiniRow>
  );
  const taskRule = (ok, text) => (
    <MiniRow className="flex-nowrap">
      <MiniIcon icon={ok ? CheckIcon : XMarkIcon} tone={ok ? "green" : "red"} />
      <MiniText>{text}</MiniText>
    </MiniRow>
  );

  return (
    <>
      <HelpIntro>{t("help.create_question.intro")}</HelpIntro>

      <HelpFlow pipeline="bts" current="questions" />

      <HelpSteps>
        {creating && inSubject && taskable && (
          <HelpStep
            title={t("help.create_question.steps.task.title")}
            illustration={
              <MiniCard tone="blue" icon={ClipboardDocumentCheckIcon}>
                <MiniRow className="justify-between">
                  <MiniText variant="strong">{t("folders.tasks.reminder.title", { count: 1 })}</MiniText>
                  <MiniText variant="muted">{t("folders.tasks.reminder.hide")}</MiniText>
                </MiniRow>
                <MiniDivider />
                <MiniRow>
                  <MiniText variant="strong">{ex("topic")}</MiniText>
                  <MiniText variant="muted">· {t("questions_page.filters.grade_n", { grade: 9 })}</MiniText>
                </MiniRow>
                <MiniRow>
                  <MiniText variant="muted">{t("folders.tasks.reminder.subtopics")}</MiniText>
                  <MiniText>{t("folders.tasks.reminder.any_subtopic")}</MiniText>
                </MiniRow>
                <MiniRow>
                  {taskLevel("A", 2, 1)}
                  {taskLevel("B", 3, 0)}
                </MiniRow>
                <MiniCard mark={{ pointer: false }}>
                  <MiniText variant="strong">{t("folders.tasks.reminder.rules_title")}</MiniText>
                  {taskRule(true, t("folders.tasks.reminder.rule_topic"))}
                  {taskRule(true, t("folders.tasks.reminder.rule_approved"))}
                  {taskRule(false, t("folders.tasks.reminder.rule_not_counted"))}
                </MiniCard>
              </MiniCard>
            }
          >
            <HelpText i18nKey="help.create_question.steps.task.text" values={{ panel: t("folders.tasks.reminder.title", { count: 1 }) }} />
            <p>
              <HelpText
                i18nKey="help.create_question.steps.task.text_rules"
                values={{ rules: t("folders.tasks.reminder.rules_title"), finish }}
              />
            </p>
            <p>
              <HelpText
                i18nKey="help.create_question.steps.task.text_open"
                values={{ page: t("nav.my_tasks"), write: t("folders.tasks.write_question") }}
              />
            </p>
          </HelpStep>
        )}

        {creating && (
          <HelpStep
            title={t("help.create_question.steps.grade.title")}
            illustration={
              <MiniCard>
                <MiniRow>
                  <MiniText variant="strong">{t("my_questions.create.select_class_and_lesson")}</MiniText>
                </MiniRow>
                <MiniRow>
                  <MiniField select value={t("my_questions.create.grade_n", { grade: 9 })} mark="1" className="flex-1" />
                </MiniRow>
                <MiniRow>
                  <MiniField select value={ex("subject")} className="flex-1" />
                </MiniRow>
                {/* Back leaves the form: on the left, or under Next on phones. */}
                {roomy ? (
                  <MiniRow className="justify-between gap-y-3">
                    {backButton}
                    <MiniButton variant="primary" mark="2">
                      {next}
                    </MiniButton>
                  </MiniRow>
                ) : (
                  <MiniStack>
                    <MiniRow>
                      <MiniButton variant="primary" mark={{ label: "2" }}>
                        {next}
                      </MiniButton>
                    </MiniRow>
                    <MiniRow>{backButton}</MiniRow>
                  </MiniStack>
                )}
              </MiniCard>
            }
          >
            <HelpText i18nKey={inSubject ? "help.create_question.steps.grade.text" : "help.create_question.steps.grade.text_free"} values={{ next }} />
            <p>{t("help.create_question.steps.grade.text_topics")}</p>
            <p>
              <HelpText i18nKey="help.create_question.steps.grade.text_back" values={{ back }} />
            </p>
          </HelpStep>
        )}

        <HelpStep
          title={t("help.create_question.steps.classify.title")}
          illustration={
            <MiniCard>
              <MiniRow className="flex-nowrap">
                <MiniField select placeholder={t("my_questions.editor.select_term")} className="flex-1" />
                <MiniField select value={ex("topic")} className="flex-1" />
              </MiniRow>
              <MiniRow className="flex-nowrap gap-y-3">
                <MiniField select placeholder={t("my_questions.editor.select_subtopic")} className="flex-1" mark />
                <MiniField select placeholder={t("my_questions.editor.select_objective")} className="flex-1" />
              </MiniRow>
            </MiniCard>
          }
        >
          <p>{t("help.create_question.steps.classify.text")}</p>
          {inSubject && <p>{t("my_questions.editor.required_in_subject")}</p>}
        </HelpStep>

        <HelpStep
          title={t("help.create_question.steps.difficulty.title")}
          illustration={
            <MiniRow className="gap-y-3">
              <MiniText variant="strong">
                {t("difficulty.label")}
                {inSubject ? " *" : ""}
              </MiniText>
              <MiniTabs items={levels} current={1} mark={1} />
            </MiniRow>
          }
        >
          <HelpText i18nKey="help.create_question.steps.difficulty.text" values={{ a: levels[0], b: levels[1], c: levels[2] }} />
        </HelpStep>

        <HelpStep
          title={t("help.create_question.steps.text.title")}
          illustration={
            <MiniCard>
              <MiniStack tight>
                <MiniText variant="muted">{t("my_questions.editor.question_label")}</MiniText>
                <MiniRow className="flex-nowrap">
                  <MiniField value={own("question")} className="flex-1" />
                  <MiniButton icon={PhotoIcon} />
                </MiniRow>
              </MiniStack>
              <MiniStack tight>
                <MiniText variant="muted">{t("my_questions.editor.answer_options")}</MiniText>
                {option("A", own("option_a"))}
              </MiniStack>
              <MiniRow className="gap-y-3">
                <MiniButton variant="link" icon={PlusIcon} mark>
                  {addOption}
                </MiniButton>
              </MiniRow>
            </MiniCard>
          }
        >
          <HelpText i18nKey="help.create_question.steps.text.text" values={{ add: addOption }} />
          <p>{t("help.create_question.steps.text.text_more")}</p>
        </HelpStep>

        <HelpStep
          title={t("help.create_question.steps.correct.title")}
          illustration={
            <MiniCard>
              {option("A", own("option_a"))}
              {option("B", own("option_b"), true)}
            </MiniCard>
          }
        >
          <HelpText
            i18nKey={wide ? "help.create_question.steps.correct.text" : "help.create_question.steps.correct.text_phone"}
            values={{ correct: correctLabel }}
          />
          <p>{t("help.create_question.steps.correct.text_order")}</p>
        </HelpStep>

        {(creating || linked) && (
          <HelpStep
            title={
              linked && !creating
                ? t("help.create_question.steps.passage.title_linked")
                : t("help.create_question.steps.passage.title")
            }
            illustration={
              linked ? (
                <MiniStack>
                  <MiniCard tone="blue" icon={BookOpenIcon} mark={{ pointer: false }}>
                    <MiniText variant="strong" truncate>
                      {t("passages.panel.title", { name: ex("passage") })}
                    </MiniText>
                  </MiniCard>
                  <MiniRow>
                    <MiniField label={t("my_questions.editor.question_label")} value={own("question")} className="flex-1" />
                  </MiniRow>
                </MiniStack>
              ) : (
                // The passage opened on the Texts tab: its dialog's questions section has the create link.
                <MiniDialog title={t("passages.dialog.title")}>
                  <MiniCard tone="slate" icon={BookOpenIcon}>
                    <MiniText truncate>{ex("passage")}</MiniText>
                  </MiniCard>
                  <MiniDivider />
                  <MiniRow className="justify-between gap-y-3">
                    <MiniText variant="strong">{t("passages.dialog.questions")}</MiniText>
                    <MiniButton variant="link" icon={PlusIcon} mark>
                      {t("passages.dialog.create_question")}
                    </MiniButton>
                  </MiniRow>
                </MiniDialog>
              )
            }
          >
            {linked ? (
              t("help.create_question.steps.passage.text_linked")
            ) : (
              <HelpText
                i18nKey="help.create_question.steps.passage.text"
                values={{ texts: t("passages.tab_texts"), create: t("passages.dialog.create_question") }}
              />
            )}
          </HelpStep>
        )}

        <HelpStep
          title={editing ? t("help.create_question.steps.save.title_edit") : t("help.create_question.steps.save.title")}
          illustration={
            <MiniStack>
              <MiniCard>
                <MiniText variant="strong">1. {own("question")}</MiniText>
                <MiniRow>
                  <MiniBadge tone="green" icon={CheckIcon}>
                    B
                  </MiniBadge>
                  <MiniText>{own("option_b")}</MiniText>
                </MiniRow>
              </MiniCard>
              {/* The final view of the create page has Back; the draft and edit pages have none. */}
              {editing ? footer(save) : footer(finish, draft, creating)}
            </MiniStack>
          }
        >
          {editing ? (
            <HelpText
              i18nKey="help.create_question.steps.save.text_edit"
              values={{ save, resubmit: t("question_detail.transitions.resubmit") }}
            />
          ) : (
            <HelpText
              i18nKey={creating ? "help.create_question.steps.save.text" : "help.create_question.steps.save.text_draft"}
              values={{ draft, next, finish }}
            />
          )}
        </HelpStep>
      </HelpSteps>

      <HelpRoles
        rows={[
          { key: "admin", role: t("folders.roles.admin"), Icon: KeyIcon, can: t("help.create_question.roles.admin") },
          { key: "moderator", role: t("folders.roles.moderator"), Icon: ShieldCheckIcon, can: t("help.create_question.roles.moderator") },
          // Experts write questions in their subject too (canCreateIn) and edit all of its questions.
          { key: "expert", role: t("folders.roles.expert"), Icon: CheckBadgeIcon, can: t("help.create_question.roles.expert") },
          { key: "author", role: t("folders.roles.author"), Icon: PencilIcon, can: t("help.create_question.roles.author") },
        ]}
      />

      <HelpTips
        items={[
          creating && (
            <HelpText key="several" i18nKey="help.create_question.tips.several" values={{ add: t("my_questions.editor.add_question") }} />
          ),
          // The create form checks a draft's text and options before keeping it (the draft editor does not).
          creating && t("help.create_question.tips.draft_basics"),
          t("help.create_question.tips.preview"),
          editing && t("help.create_question.tips.booklet"),
          mode === "draft" && t("help.create_question.tips.draft_own"),
        ]}
      />
    </>
  );
}
